import { Button, SpinningButton } from "Components/ReactstrapSlim";
import React, { useEffect, useState } from "react";
import classes from "./styles.module.scss";

import ReservationMethodSelector from "../ReservationMethodSelector";
import SpaceTypeExplainerTooltip from "../SpaceTypeExplainer";
import SpaceTypeSelector from "../SpaceTypeSelector";
import { UseFormReturn, useForm } from "Hooks";
import { yupResolver } from "@hookform/resolvers/yup";
import { FieldPath, SubmitErrorHandler, SubmitHandler } from "react-hook-form";
import * as yup from "yup";
import { Shape } from "Validation/yup/Shape";
import { ConciergeRequestClient, ConciergeRequestViewModel, IConciergeRequestViewModel, SpaceType, WorkspaceReservationMethod } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { isProblemDetails, isResponseWrapper } from "Utils/typeGuards";
import useUnmountedSignal from "Hooks/useUnmountedSignal";
import { useDispatch, useSelector } from "react-redux";
import { isAuthenticatedSelector, userSelector } from "Utils/selectors";
import { EmailInputFL, InputFL, ValidationSummary } from "Components/Inputs";

import getEnumKeys from "Utils/getEnumKeys";
import { getWorkspaceFilterViewModel } from "./getWorkspaceFilterViewModel";
import { ArrowLeftAlt } from "Components/Icons";
import classNames from "classnames";

type RequirementsEditModel = Pick<IConciergeRequestViewModel, "spaceTypes" | "reservationMethod">;
type EditModel = Omit<IConciergeRequestViewModel, "venueId" | "searchFilter">;

const RequirementsEditModelSchema = yup.object<Shape<RequirementsEditModel>>({
   spaceTypes: yup
      .number()
      .test("space-types", "Select option", (value, context) => !!value && (value & (SpaceType.Desk | SpaceType.Office | SpaceType.Meeting | SpaceType.EventSpace)) > 0),
   reservationMethod: yup
      .number()
      .oneOf(getEnumKeys(WorkspaceReservationMethod), "Select option")
});

const EditModelSchema = RequirementsEditModelSchema.concat(yup.object<Shape<EditModel>>({
   fullName: yup.string().trim().nullable()
      .when("$isAuthenticated", {
         is: false,
         then: schema => schema.fullName(true, "Full name is required")
      }),
   email: yup.string().trim().nullable()
      .when("$isAuthenticated", {
         is: false,
         then: schema => schema.required("Email is required").email("Email is not valid")
      }),
   company: yup.string().optional(),
   phoneNumber: yup.string().phone("Phone number is not valid").optional()
}));

const defaultValues: EditModel = {
   spaceTypes: SpaceType.Any,
   reservationMethod: -1 as any, //intintionally invalid,
   company: undefined,
   email: undefined,
   fullName: undefined,
   phoneNumber: undefined
}

enum FormStep {
   Requirements,
   UserData
}

interface NotifyFormProps {
   venueId: Guid;
   onSuccess: () => void;
}

const NotifyForm: React.FC<NotifyFormProps> = ({ venueId, onSuccess }) => {
   const [formStep, setFormStep] = useState<FormStep>(FormStep.Requirements);

   const user = useSelector(userSelector);

   const formMethods = useForm<EditModel>({
      defaultValues: defaultValues,
      resolver: yupResolver(EditModelSchema),
      context: {
         isAuthenticated: user.isAuthenticated
      }
   });

   const conciergeRequestClient = useApiClient(ConciergeRequestClient);

   const [serverErrors, setServerErrors] = useState<Array<[string, string]>>([]);
   const [isLoading, setLoading] = useState(false);
   const unmountedSignal = useUnmountedSignal();
   const dispatch = useDispatch();

   const openLoginPopup = (email?: string) => {
      dispatch(LSStore.signingModalSlice.actions.open({
         initialMode: LSStore.SigningMode.Login,
         onlyHeaderReload: true,
         initialEmail: email
      }));
   }

   const submitHandler: SubmitHandler<EditModel> = async data => {
      setLoading(true);
      try {
         await conciergeRequestClient.notifyMe(new ConciergeRequestViewModel({
            venueId,
            searchFilter: getWorkspaceFilterViewModel(),
            ...data,
         }));
         onSuccess();
      }
      catch (ex) {
         if (isProblemDetails(ex)) {
            if (ex.status === 401) {
               openLoginPopup(data.email);
            }
         }
         else if (isResponseWrapper(ex)) {
            if (ex.validationErrors) {
               for (const propertyName in ex.validationErrors) {
                  formMethods.setError(propertyName as FieldPath<EditModel>, { message: ex.validationErrors[propertyName][0] })
               }
            }
         }
      }
      finally {
         unmountedSignal.aborted || setLoading(false);
      }
   };

   const handleSubmitError: SubmitErrorHandler<EditModel> = async errors => {
      if (LiquidSpaceConfig.DEBUG) {
         console.log(errors);
      }

      if (await RequirementsEditModelSchema.isValid(formMethods.getValues())) {
         unmountedSignal.aborted || setFormStep(FormStep.UserData);
      }
   };

   //handle login
   useEffect(() => {
      if (formStep == FormStep.UserData && user.isAuthenticated) {
         const formValues = formMethods.getValues();
         if (user.email?.toLowerCase() != formValues.email?.toLowerCase()) {
            formMethods.reset({
               spaceTypes: formValues.spaceTypes,
               reservationMethod: formValues.reservationMethod
            });
            setFormStep(FormStep.Requirements);
         }

         formMethods.handleSubmit(submitHandler, handleSubmitError)();
      }
   }, [formStep, user]);

   return <form className={classes.form} onSubmit={formMethods.handleSubmit(submitHandler, handleSubmitError)}>
      <div className={classNames(classes.formStep, { [classes.formStepActive]: formStep == FormStep.Requirements })}>
         <div className={classes.formTitle}>
            Tell us about 
            <br/>
            your space needs.
         </div>
         <ValidationSummary errors={formMethods.formState.errors} serverErrors={serverErrors} className={classes.validationSummary} />
         <div className={classes.fields}>
            <div className={classes.field}>
               <div className={classes.fieldLabel}>
                  Choose the type of space you need
                  <SpaceTypeExplainerTooltip />
               </div>
               <SpaceTypeSelector disabled={isLoading} control={formMethods.control} name="spaceTypes" />
            </div>
            <div className={classes.field}>
               <div className={classes.fieldLabel}>
                  Choose how long you need the space for
               </div>
               <ReservationMethodSelector disabled={isLoading} control={formMethods.control} name="reservationMethod" />
            </div>
         </div>
         <div className={classes.buttons}>
            <SubmitRequirements formMethods={formMethods} isLoading={isLoading} goToUserData={() => setFormStep(FormStep.UserData)} />
         </div>
      </div>
      <div className={classNames(classes.formStep, { [classes.formStepActive]: formStep == FormStep.UserData })}>
         <div className={classes.formTitle}>Get notified.</div>
         <div className={classes.formSubTitle}>Be the first to know when this space is available for booking on LiquidSpace</div>
         <ValidationSummary errors={formMethods.formState.errors} serverErrors={serverErrors} className={classes.validationSummary} />
         <div className={classes.userDataFields}>
            <InputFL className={classes.userDataField} control={formMethods.control} name="fullName" placeholder="Full name" />
            <EmailInputFL className={classes.userDataField} control={formMethods.control} name="email" placeholder="Email" />
            <InputFL className={classes.userDataField} control={formMethods.control} name="phoneNumber" placeholder="Phone number" />
            <InputFL className={classes.userDataField} control={formMethods.control} name="company" placeholder="Company name" />
         </div>
         <div className={classes.buttons}>
            <SpinningButton isLoading={isLoading} size="lg" color="primary" className={classes.submitButton} type="submit">Notify me</SpinningButton>
            <Button color="link" disabled={isLoading} onClick={ev => { ev.preventDefault(); setFormStep(FormStep.Requirements); }} className={classes.backButton}><ArrowLeftAlt /> Back</Button>
         </div>
      </div>
   </form >
}

export default NotifyForm;

interface SubmitProps {
   formMethods: UseFormReturn<EditModel>;
   isLoading: boolean;
   goToUserData: () => void;
}

const SubmitRequirements: React.FC<SubmitProps> = ({ formMethods, isLoading, goToUserData }) => {
   const [isValid, setValid] = useState(false);
   formMethods.watch(async data => setValid(await RequirementsEditModelSchema.isValid(data)));

   const isAuthenticated = useSelector(isAuthenticatedSelector);

   return <SpinningButton isLoading={isLoading} disabled={!isValid} size="lg" color="primary" className={classes.submitButton} type="submit"
      onClick={ev => {
         if (!isAuthenticated) {
            ev.preventDefault();

            if (isValid) {
               goToUserData();
            }
         }
      }}>
      {isAuthenticated ? "Notify me" : "Continue"}
   </SpinningButton>
}
